import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { 
  setLanguage, 
  getLanguage, 
  setGameBaseUrl, 
  setAdminBaseUrl, 
  getSelectedGameBaseUrl, 
  getSelectedAdminBaseUrl, 
  getBaseUrls 
} from '../services/api';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { signOut, user, loading: authLoading, role } = useAuth();
  const { showError } = useError();
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(getLanguage());
  const [selectedGameEnv, setSelectedGameEnv] = useState<'prod' | 'stage'>('stage');
  const [selectedAdminEnv, setSelectedAdminEnv] = useState<'prod' | 'stage'>('stage');
  const baseUrls = getBaseUrls();

  useEffect(() => {
    console.log('Admin role:', role);
    
    // Initialize environment selections based on role
    if (role === 'admin') {
      // For admin users, use the selected URLs from localStorage
      const gameUrl = getSelectedGameBaseUrl();
      const adminUrl = getSelectedAdminBaseUrl();
      
      setSelectedGameEnv(gameUrl.includes('Stage') ? 'stage' : 'prod');
      setSelectedAdminEnv(adminUrl.includes('Stage') ? 'stage' : 'prod');
    } else {
      // For non-admin users, always use stage
      setSelectedGameEnv('stage');
      setSelectedAdminEnv('stage');
    }
    
    if (!authLoading && !user) {
      console.log('User not authenticated, redirecting to login');
      navigate('/admin/login', { replace: true });
    }
  }, [authLoading, user, navigate, role]);

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      navigate('/admin/login');
    } catch (err) {
      console.error('Sign out error:', err);
      showError('Failed to sign out. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    setLanguage(newLanguage);
  };

  const handleGameEnvChange = (event: SelectChangeEvent) => {
    const newEnv = event.target.value as 'prod' | 'stage';
    setSelectedGameEnv(newEnv);
    setGameBaseUrl(newEnv);
  };

  const handleAdminEnvChange = (event: SelectChangeEvent) => {
    const newEnv = event.target.value as 'prod' | 'stage';
    setSelectedAdminEnv(newEnv);
    setAdminBaseUrl(newEnv);
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null; // Will be handled by useEffect
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Admin Dashboard
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSignOut}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Out'}
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Welcome, <Box component="span" sx={{ fontWeight: 'bold' }}>{user?.email}</Box> ({role})
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Settings
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel id="language-selector-label">Language</InputLabel>
                  <Select
                    labelId="language-selector-label"
                    id="language-selector"
                    value={selectedLanguage}
                    label="Language"
                    onChange={handleLanguageChange}
                  >
                    <MenuItem value="tr">Turkish</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                  </Select>
                </FormControl>
                
                {role === 'admin' && (
                  <>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel id="game-env-selector-label">Game API</InputLabel>
                  <Select
                    labelId="game-env-selector-label"
                    id="game-env-selector"
                    value={selectedGameEnv}
                    label="Game API"
                    onChange={handleGameEnvChange}
                  >
                    <MenuItem value="prod">Production</MenuItem>
                    <MenuItem value="stage">Stage</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel id="admin-env-selector-label">Admin API</InputLabel>
                  <Select
                    labelId="admin-env-selector-label"
                    id="admin-env-selector"
                    value={selectedAdminEnv}
                    label="Admin API"
                    onChange={handleAdminEnvChange}
                  >
                    <MenuItem value="prod">Production</MenuItem>
                    <MenuItem value="stage">Stage</MenuItem>
                  </Select>
                </FormControl>
                  </>
                )}
              </Box>
              
              <Box sx={{ mt: 2 }}>
                {role === 'admin' && (
                  <>
                <Typography variant="body2" color="text.secondary">
                  Base URL: <Box component="span" sx={{ fontWeight: 'bold' }}>https://{baseUrls.game[selectedGameEnv].split('https://')[1].split('/')[0]}</Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Game API: <Box component="span" sx={{ fontWeight: 'bold' }}>/{baseUrls.game[selectedGameEnv].split('https://')[1].split('/').slice(1).join('/')}</Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Admin API: <Box component="span" sx={{ fontWeight: 'bold' }}>/{baseUrls.admin[selectedAdminEnv].split('https://')[1].split('/').slice(1).join('/')}</Box>
                </Typography>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2, 
                mt: 2 
              }}>
                {role === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/decks')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  Manage Decks
                </Button>
                )}
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/packages')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  Manage Taboo Cards
                </Button>
                {role === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/docs')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  API Documentation
                </Button>
                )}
                {role === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/config')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  Config Settings
                </Button>
                )}
                {role === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/messages')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  Manage Messages
                </Button>
                )}
                {role === 'admin' && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/admin/team-names')}
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  Manage Team Names
                </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Admin; 